import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../../../context/theme";
import axiosInstance from "../../../interceptors";
import { useNavigate } from "react-router-dom";

// HR-facing bulk resume upload. Files go to amplehire_aiservice as one multipart
// batch (`POST /ai/resume-parse/upload`), then the UI polls
// `GET /ai/resume-parse/batches/{batchId}` every 8s until the batch is terminal.

const RESUME_API = "/ai/resume-parse";
const POLL_INTERVAL_MS = 8000;
const MAX_FILES = 50;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_EXT_RE = /\.(pdf|docx|txt)$/i;

const fadeUp = { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } };
const staggerContainer = { visible: { transition: { staggerChildren: 0 } } };
const rowVariant = {
  hidden: { opacity: 1, x: 0 },
  visible: { opacity: 1, x: 0 },
};

const UploadResumes = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [batchId, setBatchId] = useState(null);
  const [batchInfo, setBatchInfo] = useState(null);
  const [pollNotice, setPollNotice] = useState(null);
  const [parsed, setParsed] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [drawer, setDrawer] = useState(null);
  const pollTimerRef = useRef(null);

  // Deduplication state
  const [dupCount, setDupCount] = useState(0);
  const [dupGroups, setDupGroups] = useState([]);
  const [showDups, setShowDups] = useState(false);
  const [dupLoading, setDupLoading] = useState(false);
  const [mergeSelections, setMergeSelections] = useState({});
  const [merging, setMerging] = useState(null);
  const [dupToast, setDupToast] = useState(null);

  const loadDuplicates = async () => {
    setDupLoading(true);
    // No duplicate detection API exists yet — show empty
    setDupGroups([]);
    setDupCount(0);
    setDupLoading(false);
  };

  const mergeDuplicates = async (groupIdx) => {
    const keepId = mergeSelections[groupIdx];
    if (!keepId) {
      setDupToast("Select which candidate to keep");
      setTimeout(() => setDupToast(null), 2500);
      return;
    }
    const group = dupGroups[groupIdx];
    const otherIds = group.candidates
      .map((c) => c.id)
      .filter((id) => id !== keepId);
    setMerging(groupIdx);
    try {
      await axiosInstance.post("/ai/resumes/merge", {
        keepId,
        mergeIds: otherIds,
      });
      setDupToast("Merge complete");
      setTimeout(() => setDupToast(null), 3000);
      loadDuplicates();
      loadParsed();
    } catch (e) {
      setDupToast("Merge failed: " + (e.response?.data?.message || e.message));
      setTimeout(() => setDupToast(null), 3000);
    } finally {
      setMerging(null);
    }
  };

  // Filter & Pagination state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    q: "",
    skill: "",
    role: "",
    location: "",
    company: "",
    education: "",
    minYears: "",
    maxYears: "",
  });
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    document.title = "Resumes - Ample Hire";
    loadParsed(null, 0);
    loadDuplicates();
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadParsed = async (filterParams, pageNum) => {
    setLoadingList(true);
    try {
      const f = filterParams || filters;
      const pg = pageNum !== undefined ? pageNum : currentPage;

      // Pull the most recent batches for this tenant. Then flatten their items
      // into the candidate-style rows the table expects. v1 has no item-level
      // search API, so filters apply client-side over the fetched window.
      const res = await axiosInstance.get(`${RESUME_API}/batches`, {
        params: { page: pg, size: pageSize },
      });
      const data = res.data || {};
      const batches = Array.isArray(data.content) ? data.content : [];

      const itemArrays = await Promise.all(
        batches.map((b) =>
          axiosInstance
            .get(`${RESUME_API}/batches/${b.id}`)
            .then((r) => r.data?.items || [])
            .catch(() => []),
        ),
      );

      let items = itemArrays.flat().map(itemToCandidateRow);

      if (f.q) {
        const q = f.q.toLowerCase();
        items = items.filter(
          (c) =>
            (c.candidateName || "").toLowerCase().includes(q) ||
            (c.candidateEmail || "").toLowerCase().includes(q) ||
            (c.candidatePhone || "").includes(q) ||
            (c.fileName || "").toLowerCase().includes(q),
        );
      }
      if (f.location) {
        items = items.filter((c) =>
          (c.currentLocation || "")
            .toLowerCase()
            .includes(f.location.toLowerCase()),
        );
      }

      setParsed(items);
      setTotalElements(
        typeof data.totalElements === "number"
          ? data.totalElements
          : items.length,
      );
      setTotalPages(typeof data.totalPages === "number" ? data.totalPages : 1);
      setCurrentPage(pg);
      setActiveFilterCount(Object.values(f).filter((v) => v !== "").length);
    } catch (e) {
      console.error("Load error:", e);
      setParsed([]);
      setTotalElements(0);
      setTotalPages(0);
    } finally {
      setLoadingList(false);
    }
  };

  const applyFilters = () => {
    setCurrentPage(0);
    loadParsed(filters, 0);
  };
  const clearFilters = () => {
    const empty = {
      q: "",
      skill: "",
      role: "",
      location: "",
      company: "",
      education: "",
      minYears: "",
      maxYears: "",
    };
    setFilters(empty);
    setCurrentPage(0);
    loadParsed(empty, 0);
  };

  const onPick = (files) => {
    const arr = Array.from(files || []).filter(
      (f) => f.type === "application/pdf" || ACCEPTED_EXT_RE.test(f.name),
    );
    if (arr.length === 0) return;
    if (arr.length > MAX_FILES) {
      setPollNotice(`Too many files — max is ${MAX_FILES} per upload.`);
      return;
    }
    const oversized = arr.find((f) => f.size > MAX_FILE_BYTES);
    if (oversized) {
      setPollNotice(`"${oversized.name}" exceeds the 10 MB per-file limit.`);
      return;
    }
    uploadBatch(arr);
  };

  const uploadBatch = async (files) => {
    stopPolling();
    setPollNotice(null);

    // Optimistic queue so the user sees rows immediately, before the server
    // responds with real item ids.
    const optimistic = files.map((f, i) => ({
      id: `pending-${i}`,
      fileName: f.name,
      mimeType: f.type,
      fileSizeBytes: f.size,
      status: "QUEUED",
      extractedName: null,
      extractedEmail: null,
      extractedPhone: null,
      errorMessage: null,
      _optimistic: true,
    }));
    setQueue(optimistic);
    setBatchInfo({
      totalCount: files.length,
      completedCount: 0,
      failedCount: 0,
      status: "IN_PROGRESS",
    });

    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const res = await axiosInstance.post(`${RESUME_API}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { batchId: newBatchId, items, totalCount } = res.data || {};
      setBatchId(newBatchId);
      setQueue(Array.isArray(items) ? items : []);
      setBatchInfo((b) => ({
        ...(b || {}),
        totalCount: totalCount ?? items?.length ?? files.length,
        completedCount: 0,
        failedCount: 0,
        status: "IN_PROGRESS",
      }));
      startPolling(newBatchId);
    } catch (e) {
      const msg = e?.message || e?.data?.error || "Upload failed";
      setQueue(
        optimistic.map((o) => ({ ...o, status: "FAILED", errorMessage: msg })),
      );
      setBatchInfo((b) => ({
        ...(b || {}),
        status: "PARTIAL",
        failedCount: files.length,
      }));
      setPollNotice(msg);
    }
  };

  const startPolling = (id) => {
    stopPolling();

    const tick = async () => {
      try {
        const res = await axiosInstance.get(`${RESUME_API}/batches/${id}`);
        const { batch, items } = res.data || {};
        if (Array.isArray(items)) setQueue(items);
        if (batch) setBatchInfo(batch);
        setPollNotice(null);

        if (batch && batch.status !== "IN_PROGRESS") {
          stopPolling();
          loadParsed(filters, 0);
          return;
        }

        pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        // Network blip — show banner, retry every 5s.
        setPollNotice("Reconnecting…");
        pollTimerRef.current = setTimeout(tick, 5000);
      }
    };

    pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
  };

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    onPick(e.dataTransfer.files);
  };

  const deleteParsed = async (id) => {
    if (!window.confirm("Remove this parsed resume?")) return;
    try {
      await axiosInstance.delete(`${RESUME_API}/items/${id}`);
      setParsed((p) => p.filter((r) => r.id !== id));
    } catch (e) {
      console.error("Delete failed:", e);
    }
  };

  const deleteQueueItem = async (id) => {
    if (typeof id === "string" && id.startsWith("pending-")) return;
    try {
      await axiosInstance.delete(`${RESUME_API}/items/${id}`);
      setQueue((q) => q.filter((it) => it.id !== id));
    } catch (e) {
      console.error("Delete failed:", e);
    }
  };

  return (
    <motion.div
      className="max-w-6xl mx-auto space-y-6"
      initial="visible"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0 } } }}
    >
      <motion.div variants={fadeUp}>
        <h1 className="text-2xl font-bold text-vf-base font-heading">
          Resume Library
        </h1>
        <p className="text-sm mt-1 text-vf-muted">
          Upload candidate resumes. AI extracts skills, experience, and
          education so you can find the right candidates by asking the AI
          Assistant.
        </p>
      </motion.div>

      {/* Upload zone */}
      <motion.div
        variants={fadeUp}
        animate={dragOver ? { scale: 1.02 } : { scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-2xl p-10 text-center transition-all duration-300 border-2 border-dashed ${
          dragOver
            ? "bg-vf-tint border-vf-accent"
            : "bg-vf-tint border-vf-accent/30 hover:border-vf-accent/60"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
          multiple
          className="hidden"
          onChange={(e) => onPick(e.target.files)}
        />
        <motion.div
          className="w-14 h-14 mx-auto mb-3 rounded-2xl flex items-center justify-center bg-vf-primary text-white"
          animate={
            dragOver ? { rotate: [0, -5, 5, 0], y: -4 } : { rotate: 0, y: 0 }
          }
          transition={{ duration: 0.4 }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
          </svg>
        </motion.div>
        <p className="text-base font-semibold text-vf-base font-heading">
          Drop resumes here
        </p>
        <p className="text-xs mt-1 text-vf-muted">
          or click to choose files &middot; PDF, DOCX, TXT &middot; up to{" "}
          {MAX_FILES} files, 10 MB each
        </p>
      </motion.div>

      {/* Queue */}
      <AnimatePresence>
        {queue.length > 0 && (
          <motion.div
            className="card overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="px-5 py-3 border-b border-vf-surface">
              <div className="flex items-center justify-between text-sm font-semibold text-vf-base font-heading">
                <span>
                  {batchInfo && batchInfo.status === "IN_PROGRESS"
                    ? `Parsing ${batchInfo.completedCount + batchInfo.failedCount}/${batchInfo.totalCount}`
                    : batchInfo && batchInfo.status === "COMPLETED"
                      ? `Batch complete · ${batchInfo.completedCount}/${batchInfo.totalCount}`
                      : batchInfo && batchInfo.status === "PARTIAL"
                        ? `Finished with ${batchInfo.failedCount} failure${batchInfo.failedCount === 1 ? "" : "s"}`
                        : `Currently parsing (${queue.length})`}
                </span>
                {batchId && (
                  <span className="text-xs font-normal text-vf-muted">
                    Batch #{batchId}
                  </span>
                )}
              </div>
              {batchInfo && batchInfo.totalCount > 0 && (
                <div className="mt-2 h-1.5 w-full bg-vf-surface rounded-full overflow-hidden flex">
                  <div
                    className="bg-vf-success h-full transition-all duration-500"
                    style={{
                      width: `${(batchInfo.completedCount / batchInfo.totalCount) * 100}%`,
                    }}
                  />
                  <div
                    className="bg-vf-danger h-full transition-all duration-500"
                    style={{
                      width: `${(batchInfo.failedCount / batchInfo.totalCount) * 100}%`,
                    }}
                  />
                </div>
              )}
              {pollNotice && (
                <div className="mt-2 text-xs text-vf-muted">{pollNotice}</div>
              )}
            </div>
            <ul>
              {queue.map((item, i) => {
                const ui = mapItemStatusToBadge(item.status);
                const primary = item.extractedName || item.fileName;
                const secondary =
                  item.status === "COMPLETED"
                    ? item.extractedEmail
                    : item.status === "PROCESSING" && item.startedAt
                      ? `Started ${formatRelative(item.startedAt)}`
                      : item.status === "FAILED"
                        ? item.errorMessage
                        : item.status === "QUEUED"
                          ? "Waiting in queue"
                          : null;
                const clickable = item.status === "COMPLETED";
                return (
                  <motion.li
                    key={item.id ?? i}
                    className={`px-5 py-3 flex items-center gap-3 text-sm border-b border-vf-surface ${clickable ? "cursor-pointer hover:bg-vf-tint" : ""}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() =>
                      clickable &&
                      setDrawer({ id: item.id, fileName: item.fileName })
                    }
                  >
                    <StatusBadge status={ui} />
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-vf-base">{primary}</div>
                      {secondary && (
                        <div
                          className={`text-xs truncate ${item.status === "FAILED" ? "text-vf-danger" : "text-vf-muted"}`}
                        >
                          {secondary}
                        </div>
                      )}
                    </div>
                    {item.status === "FAILED" && !item._optimistic && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteQueueItem(item.id);
                        }}
                        className="btn-ghost btn-sm text-xs"
                      >
                        Remove
                      </button>
                    )}
                  </motion.li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Parsed list */}
      {/* Filter Bar */}
      <motion.div className="card overflow-hidden" variants={fadeUp}>
        <div className="px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Quick search */}
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 text-vf-muted"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                className="input pl-9 w-64"
                placeholder="Search by name, email, skill..."
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              />
            </div>
            <button onClick={applyFilters} className="btn-primary btn-sm">
              Search
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn-ghost btn-sm flex items-center gap-1.5 ${showFilters ? "text-vf-primary" : ""}`}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              Filters
              {activeFilterCount > 0 && (
                <span className="bg-vf-primary text-white text-2xs rounded-full w-4 h-4 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="text-xs text-vf-muted hover:text-vf-danger transition-colors"
              >
                Clear all
              </button>
            )}
            {dupCount > 0 && (
              <button
                onClick={() => setShowDups(!showDups)}
                className={`btn-sm text-xs flex items-center gap-1.5 ${showDups ? "btn-primary" : "btn-secondary"}`}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="8" y="2" width="13" height="13" rx="2" />
                  <path d="M3 9a2 2 0 012-2h1m0 12h10a2 2 0 002-2v-1" />
                  <rect x="3" y="9" width="13" height="13" rx="2" />
                </svg>
                {dupCount} duplicate{dupCount !== 1 ? "s" : ""} found
              </button>
            )}
          </div>
          <div className="text-xs text-vf-muted">
            {totalElements} candidates total
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && (
          <div className="px-5 py-4 border-t bg-vf-tint">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="input-label">Skill</label>
                <input
                  className="input"
                  placeholder="e.g. react, java"
                  value={filters.skill}
                  onChange={(e) =>
                    setFilters({ ...filters, skill: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="input-label">Role</label>
                <input
                  className="input"
                  placeholder="e.g. backend engineer"
                  value={filters.role}
                  onChange={(e) =>
                    setFilters({ ...filters, role: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="input-label">Location</label>
                <input
                  className="input"
                  placeholder="e.g. Bangalore"
                  value={filters.location}
                  onChange={(e) =>
                    setFilters({ ...filters, location: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="input-label">Company</label>
                <input
                  className="input"
                  placeholder="e.g. Groww"
                  value={filters.company}
                  onChange={(e) =>
                    setFilters({ ...filters, company: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="input-label">Education</label>
                <input
                  className="input"
                  placeholder="e.g. Bachelors, Masters"
                  value={filters.education}
                  onChange={(e) =>
                    setFilters({ ...filters, education: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="input-label">Min Experience (yrs)</label>
                <input
                  className="input"
                  type="number"
                  placeholder="0"
                  value={filters.minYears}
                  onChange={(e) =>
                    setFilters({ ...filters, minYears: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="input-label">Max Experience (yrs)</label>
                <input
                  className="input"
                  type="number"
                  placeholder="20"
                  value={filters.maxYears}
                  onChange={(e) =>
                    setFilters({ ...filters, maxYears: e.target.value })
                  }
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={applyFilters}
                  className="btn-primary btn-sm w-full"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Duplicates Section */}
      <AnimatePresence>
        {showDups && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="card overflow-hidden"
          >
            <div className="px-5 py-3 border-b border-vf-surface flex items-center justify-between bg-vf-tint">
              <h2 className="text-sm font-semibold text-vf-base font-heading">
                Duplicate Groups ({dupGroups.length})
              </h2>
              <button
                onClick={() => setShowDups(false)}
                className="btn-ghost btn-sm text-xs"
              >
                Close
              </button>
            </div>
            {dupLoading ? (
              <div className="p-8 text-center">
                <div className="skeleton h-4 w-32 mx-auto" />
              </div>
            ) : dupGroups.length === 0 ? (
              <div className="p-8 text-center text-sm text-vf-muted">
                No duplicates found.
              </div>
            ) : (
              <div className="divide-y divide-vf-surface">
                {dupGroups.map((group, gi) => (
                  <div key={gi} className="px-5 py-4">
                    <div className="flex items-center gap-2 mb-3">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="text-vf-muted"
                      >
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                      <span className="text-sm font-medium text-vf-base">
                        {group.email || "Unknown email"}
                      </span>
                      <span className="badge-warning text-[10px]">
                        {group.candidates?.length || 0} duplicates
                      </span>
                    </div>
                    <div className="space-y-2 ml-5">
                      {(group.candidates || []).map((c) => (
                        <label
                          key={c.id}
                          className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                            mergeSelections[gi] === c.id
                              ? "border-vf-primary bg-vf-tint"
                              : "border-vf-surface hover:bg-vf-bg"
                          }`}
                        >
                          <input
                            type="radio"
                            name={`merge-group-${gi}`}
                            checked={mergeSelections[gi] === c.id}
                            onChange={() =>
                              setMergeSelections({
                                ...mergeSelections,
                                [gi]: c.id,
                              })
                            }
                            className="accent-vf-primary"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-vf-base">
                              {c.candidateName || c.name || "\u2014"}
                            </div>
                            <div className="text-xs text-vf-muted">
                              {c.currentRole || c.role || "\u2014"}
                              {c.createdAt && (
                                <span className="ml-2 opacity-70">
                                  Added{" "}
                                  {new Date(c.createdAt).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                          {mergeSelections[gi] === c.id && (
                            <span className="badge-success text-[10px]">
                              Keep
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                    <div className="mt-3 ml-5">
                      <button
                        onClick={() => mergeDuplicates(gi)}
                        disabled={!mergeSelections[gi] || merging === gi}
                        className="btn-primary btn-sm disabled:opacity-40"
                      >
                        {merging === gi
                          ? "Merging..."
                          : "Merge (keep selected)"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dup toast */}
      <AnimatePresence>
        {dupToast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 right-6 z-[9999] card px-5 py-3 text-sm font-medium text-vf-base shadow-lg"
          >
            {dupToast}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Parsed Candidates Table */}
      <motion.div className="card overflow-hidden" variants={fadeUp}>
        <div className="px-5 py-3 border-b border-vf-surface flex items-center justify-between">
          <h2 className="text-sm font-semibold text-vf-base font-heading">
            Parsed candidates{" "}
            {parsed.length > 0 && (
              <span className="text-vf-muted">({parsed.length})</span>
            )}
          </h2>
          <button onClick={loadParsed} className="btn-ghost btn-sm">
            Refresh
          </button>
        </div>
        {loadingList ? (
          <div className="px-5 py-10 text-center">
            <div className="skeleton h-4 w-24 mx-auto rounded" />
          </div>
        ) : parsed.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-vf-muted">
            No resumes parsed yet. Upload some PDFs above to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-fixed">
              <thead>
                <tr className="tbl-header">
                  <th className="text-left px-4 py-3 w-[16%]">Name</th>
                  <th className="text-left px-4 py-3 w-[18%]">Role</th>
                  <th className="text-left px-4 py-3 w-[10%]">Experience</th>
                  <th className="text-left px-4 py-3 w-[12%]">Location</th>
                  <th className="text-left px-4 py-3 w-[22%]">Email</th>
                  <th className="text-left px-4 py-3 w-[10%]">Status</th>
                  <th className="px-4 py-3 w-[12%]"></th>
                </tr>
              </thead>
              <motion.tbody
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
              >
                {parsed.map((c) => (
                  <motion.tr
                    key={c.id}
                    variants={rowVariant}
                    onClick={() => setDrawer(c)}
                    className="tbl-row cursor-pointer"
                  >
                    <td className="px-4 py-3 font-medium truncate text-vf-base">
                      {c.candidateName || c.fileName || "\u2014"}
                    </td>
                    <td className="px-4 py-3 truncate text-vf-muted">
                      {c.currentRole || "\u2014"}
                    </td>
                    <td className="px-4 py-3 text-vf-muted">
                      {c.totalYearsExperience != null
                        ? `${c.totalYearsExperience} yrs`
                        : "\u2014"}
                    </td>
                    <td className="px-4 py-3 truncate text-vf-muted">
                      {c.currentLocation || "\u2014"}
                    </td>
                    <td className="px-4 py-3 truncate text-vf-muted">
                      {c.candidateEmail || "\u2014"}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={mapItemStatusToBadge(c.status)} />
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/admin/talent-pools?action=invite&candidateId=${c.id}`,
                          );
                        }}
                        className="btn-primary btn-sm mr-2"
                        disabled={c.status !== "COMPLETED"}
                      >
                        Invite
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteParsed(c.id);
                        }}
                        className="btn-danger btn-sm"
                      >
                        Remove
                      </button>
                    </td>
                  </motion.tr>
                ))}
              </motion.tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-xs text-vf-muted">
            Showing {currentPage * pageSize + 1}–
            {Math.min((currentPage + 1) * pageSize, totalElements)} of{" "}
            {totalElements} candidates
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => {
                setCurrentPage(0);
                loadParsed(filters, 0);
              }}
              disabled={currentPage === 0}
              className="btn-ghost btn-sm disabled:opacity-30"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="11 17 6 12 11 7" />
                <polyline points="18 17 13 12 18 7" />
              </svg>
            </button>
            <button
              onClick={() => {
                const p = currentPage - 1;
                setCurrentPage(p);
                loadParsed(filters, p);
              }}
              disabled={currentPage === 0}
              className="btn-ghost btn-sm disabled:opacity-30"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pageNum;
              if (totalPages <= 7) {
                pageNum = i;
              } else if (currentPage < 3) {
                pageNum = i;
              } else if (currentPage > totalPages - 4) {
                pageNum = totalPages - 7 + i;
              } else {
                pageNum = currentPage - 3 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => {
                    setCurrentPage(pageNum);
                    loadParsed(filters, pageNum);
                  }}
                  className={`btn-sm min-w-[32px] ${currentPage === pageNum ? "btn-primary" : "btn-ghost"}`}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            <button
              onClick={() => {
                const p = currentPage + 1;
                setCurrentPage(p);
                loadParsed(filters, p);
              }}
              disabled={currentPage >= totalPages - 1}
              className="btn-ghost btn-sm disabled:opacity-30"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
            <button
              onClick={() => {
                const p = totalPages - 1;
                setCurrentPage(p);
                loadParsed(filters, p);
              }}
              disabled={currentPage >= totalPages - 1}
              className="btn-ghost btn-sm disabled:opacity-30"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="13 17 18 12 13 7" />
                <polyline points="6 17 11 12 6 7" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {drawer &&
        createPortal(
          <CandidateDetailView
            candidate={drawer}
            onClose={() => setDrawer(null)}
            onUpdated={loadParsed}
          />,
          document.body,
        )}
    </motion.div>
  );
};

const NOTE_TYPES = [
  { value: "COMMENT", label: "Comment", badge: "badge-brand" },
  { value: "PROMISE", label: "Promise", badge: "badge-warning" },
  { value: "INTERNAL", label: "Internal", badge: "badge-danger" },
];

const EDITABLE_FIELDS = [
  { key: "candidateName", label: "Full Name", type: "text" },
  { key: "candidateEmail", label: "Email", type: "email" },
  { key: "candidatePhone", label: "Phone", type: "text" },
  { key: "currentRole", label: "Current Role", type: "text" },
  { key: "currentCompany", label: "Company", type: "text" },
  { key: "totalYearsExperience", label: "Experience (years)", type: "number" },
  { key: "currentLocation", label: "Location", type: "text" },
  { key: "preferredLocation", label: "Preferred Location", type: "text" },
  { key: "noticePeriodDays", label: "Notice Period (days)", type: "number" },
  { key: "currentCtcLpa", label: "Current CTC (LPA)", type: "number" },
  { key: "expectedCtcLpa", label: "Expected CTC (LPA)", type: "number" },
  { key: "educationLevel", label: "Education Level", type: "text" },
  { key: "linkedinUrl", label: "LinkedIn URL", type: "url" },
  { key: "githubUrl", label: "GitHub URL", type: "url" },
  { key: "portfolioUrl", label: "Portfolio URL", type: "url" },
];

const JOURNEY_EVENT_STYLES = {
  RESUME_UPLOADED: { color: "bg-blue-500", label: "Resume Uploaded" },
  INVITE_SENT: { color: "bg-purple-500", label: "Invite Sent" },
  INVITE_ACCEPTED: { color: "bg-green-500", label: "Invite Accepted" },
  INVITE_REJECTED: { color: "bg-red-500", label: "Invite Rejected" },
  INTERVIEW_STARTED: { color: "bg-yellow-500", label: "Interview Started" },
  INTERVIEW_COMPLETED: { color: "bg-blue-500", label: "Interview Completed" },
  INTERVIEW_SCORED: { color: "bg-green-500", label: "Interview Scored" },
  FEEDBACK_TAGGED: { color: "bg-vf-primary", label: "Feedback Tagged" },
};

const CandidateDetailView = ({ candidate, onClose, onUpdated }) => {
  const { isDark } = useTheme();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [noteType, setNoteType] = useState("COMMENT");
  const [addingNote, setAddingNote] = useState(false);
  const [journey, setJourney] = useState([]);
  const [loadingJourney, setLoadingJourney] = useState(true);
  const [fullCandidate, setFullCandidate] = useState(candidate);
  const [rawExcerpt, setRawExcerpt] = useState(null);
  const [showRawExcerpt, setShowRawExcerpt] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [detailError, setDetailError] = useState(null);

  // Fetch the full item (with parsedJson) the first time the drawer opens.
  useEffect(() => {
    let cancelled = false;
    setLoadingDetail(true);
    setDetailError(null);
    axiosInstance
      .get(`/ai/resume-parse/items/${candidate.id}`)
      .then((res) => {
        if (cancelled) return;
        const enriched = normalizeItemToCandidate(res.data);
        setFullCandidate(enriched);
        setRawExcerpt(enriched._rawTextExcerpt || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setDetailError(err?.message || "Failed to load resume details.");
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [candidate.id]);

  useEffect(() => {
    const c = fullCandidate || candidate;
    const formData = {};
    EDITABLE_FIELDS.forEach((f) => {
      formData[f.key] = c[f.key] ?? "";
    });
    formData.summary = c.summary || "";
    formData.skills = (c.skills || []).join(", ");
    formData.workExperience = (c.workExperience || []).map((w) => ({ ...w }));
    formData.education = (c.education || []).map((e) => ({ ...e }));
    formData.certifications = (c.certifications || []).map((cert) => ({
      ...cert,
    }));
    setForm(formData);
  }, [fullCandidate, candidate]);

  // const loadNotes = useCallback(async () => {
  //   setLoadingNotes(true);
  //   try {
  //     const r = await axiosInstance.get(`/ai/candidates/${candidate.id}/notes`);
  //     setNotes(r.data || []);
  //   } catch { setNotes([]); }
  //   finally { setLoadingNotes(false); }
  // }, [candidate.id]);

  //useEffect(() => { loadNotes(); }, [loadNotes]);

  // useEffect(() => {
  //   setLoadingJourney(true);
  //   axiosInstance.get(`/ai/candidates/${candidate.id}/journey`)
  //     .then((r) => { const d = r.data; setJourney(Array.isArray(d) ? d : d?.timeline || []); })
  //     .catch(() => setJourney([]))
  //     .finally(() => setLoadingJourney(false));
  // }, [candidate.id]);

  const handleChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleListChange = (listKey, index, field, value) => {
    setForm((f) => {
      const arr = [...(f[listKey] || [])];
      arr[index] = { ...arr[index], [field]: value };
      return { ...f, [listKey]: arr };
    });
  };

  const addListItem = (listKey, template) => {
    setForm((f) => ({
      ...f,
      [listKey]: [...(f[listKey] || []), { ...template }],
    }));
  };

  const removeListItem = (listKey, index) => {
    setForm((f) => ({
      ...f,
      [listKey]: (f[listKey] || []).filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (typeof payload.skills === "string") {
        payload.skills = payload.skills
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      if (payload.totalYearsExperience !== "")
        payload.totalYearsExperience = Number(payload.totalYearsExperience);
      if (payload.noticePeriodDays !== "")
        payload.noticePeriodDays = Number(payload.noticePeriodDays);
      if (payload.currentCtcLpa !== "")
        payload.currentCtcLpa = Number(payload.currentCtcLpa);
      if (payload.expectedCtcLpa !== "")
        payload.expectedCtcLpa = Number(payload.expectedCtcLpa);
      await axiosInstance.put(`/ai/resumes/${candidate.id}`, payload);
      setEditing(false);
      onUpdated && onUpdated();
    } catch (err) {
      console.error("Failed to save candidate:", err);
      alert(err.response?.data?.message || "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  // const addNote = async () => {
  //   if (!noteText.trim()) return;
  //   setAddingNote(true);
  //   try {
  //     await axiosInstance.post(`/ai/candidates/${candidate.id}/notes`, {
  //       text: noteText.trim(),
  //       noteType,
  //     });
  //     setNoteText("");
  //     loadNotes();
  //   } catch (err) {
  //     console.error("Failed to add note:", err);
  //     alert(err.response?.data?.message || "Failed to add note.");
  //   } finally {
  //     setAddingNote(false);
  //   }
  // };

  // const delNote = async (id) => {
  //   if (!window.confirm("Delete this note?")) return;
  //   try {
  //     await axiosInstance.delete(`/ai/candidates/${candidate.id}/notes/${id}`);
  //     setNotes((n) => n.filter((x) => x.id !== id));
  //   } catch (err) {
  //     console.error("Failed to delete note:", err);
  //   }
  // };

  const c = fullCandidate;

  return (
    <motion.div
      className="fixed inset-0 flex flex-col bg-vf-bg z-[9999]"
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-vf-surface bg-white">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="btn-ghost btn-sm flex items-center gap-1"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>
          <div>
            <h1 className="text-xl font-bold text-vf-base font-heading">
              {c.candidateName || "Candidate"}
            </h1>
            <p className="text-xs text-vf-muted">
              {c.candidateEmail || "\u2014"}{" "}
              {c.candidatePhone ? ` \u00b7 ${c.candidatePhone}` : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <button
                onClick={() => setEditing(false)}
                className="btn-ghost btn-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary btn-sm"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="btn-secondary btn-sm flex items-center gap-1"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Edit
            </button>
          )}
          {(c.linkedinUrl || c.githubUrl || c.portfolioUrl) && !editing && (
            <div className="flex items-center gap-1 ml-2">
              {c.linkedinUrl && (
                <a
                  href={c.linkedinUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost btn-sm"
                >
                  LinkedIn
                </a>
              )}
              {c.githubUrl && (
                <a
                  href={c.githubUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost btn-sm"
                >
                  GitHub
                </a>
              )}
              {c.portfolioUrl && (
                <a
                  href={c.portfolioUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost btn-sm"
                >
                  Portfolio
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <motion.div
          className="max-w-5xl mx-auto p-6 space-y-6"
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.07 } } }}
        >
          {loadingDetail && (
            <div className="card p-5 text-center text-sm text-vf-muted">
              Loading resume details…
            </div>
          )}
          {detailError && !loadingDetail && (
            <div className="card p-5 text-center text-sm text-vf-danger">
              {detailError}
            </div>
          )}

          {/* Summary */}
          {(c.summary || editing) && (
            <motion.div className="card p-5" variants={fadeUp}>
              <SectionHeader>AI Summary</SectionHeader>
              {editing ? (
                <textarea
                  value={form.summary}
                  onChange={(e) => handleChange("summary", e.target.value)}
                  rows={3}
                  className="textarea w-full mt-1"
                  placeholder="Candidate summary..."
                />
              ) : (
                <p className="text-sm leading-relaxed mt-1 text-vf-base">
                  {c.summary}
                </p>
              )}
            </motion.div>
          )}

          {/* Personal & Professional Info */}
          <motion.div className="card p-5" variants={fadeUp}>
            <SectionHeader>Candidate Information</SectionHeader>
            {editing ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                {EDITABLE_FIELDS.map((f) => (
                  <div key={f.key}>
                    <label className="input-label">{f.label}</label>
                    <input
                      type={f.type}
                      value={form[f.key] ?? ""}
                      onChange={(e) => handleChange(f.key, e.target.value)}
                      className="input w-full mt-1"
                      placeholder={f.label}
                    />
                  </div>
                ))}
                <div className="md:col-span-3">
                  <label className="input-label">
                    Skills (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={form.skills}
                    onChange={(e) => handleChange("skills", e.target.value)}
                    className="input w-full mt-1"
                    placeholder="react, typescript, node.js"
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 mt-3">
                <InfoField label="Current Role" value={c.currentRole} />
                <InfoField label="Company" value={c.currentCompany} />
                <InfoField
                  label="Experience"
                  value={
                    c.totalYearsExperience != null
                      ? `${c.totalYearsExperience} yrs`
                      : null
                  }
                />
                <InfoField label="Location" value={c.currentLocation} />
                <InfoField label="Phone" value={c.candidatePhone} />
                <InfoField
                  label="Preferred Location"
                  value={c.preferredLocation}
                />
                <InfoField
                  label="Notice Period"
                  value={
                    c.noticePeriodDays != null
                      ? `${c.noticePeriodDays} days`
                      : null
                  }
                />
                <InfoField
                  label="Current CTC"
                  value={
                    c.currentCtcLpa != null
                      ? `\u20B9${c.currentCtcLpa} LPA`
                      : null
                  }
                />
                <InfoField
                  label="Expected CTC"
                  value={
                    c.expectedCtcLpa != null
                      ? `\u20B9${c.expectedCtcLpa} LPA`
                      : null
                  }
                />
                <InfoField label="Education" value={c.educationLevel} />
              </div>
            )}
          </motion.div>

          {/* Skills */}
          {!editing && c.skills && c.skills.length > 0 && (
            <motion.div className="card p-5" variants={fadeUp}>
              <SectionHeader>Skills</SectionHeader>
              <div className="flex flex-wrap gap-2 mt-2">
                {c.skills.map((s, i) => (
                  <span key={i} className="badge-brand">
                    {s}
                  </span>
                ))}
              </div>
            </motion.div>
          )}

          {/* Work Experience */}
          {(editing || (c.workExperience && c.workExperience.length > 0)) && (
            <motion.div className="card p-5" variants={fadeUp}>
              <div className="flex items-center justify-between">
                <SectionHeader>Work Experience</SectionHeader>
                {editing && (
                  <button
                    onClick={() =>
                      addListItem("workExperience", {
                        title: "",
                        company: "",
                        location: "",
                        start_date: "",
                        end_date: "",
                        is_current: false,
                        description: "",
                      })
                    }
                    className="btn-ghost btn-sm text-xs"
                  >
                    + Add
                  </button>
                )}
              </div>
              {editing ? (
                <div className="space-y-4 mt-2">
                  {(form.workExperience || []).map((w, i) => (
                    <div
                      key={i}
                      className="rounded-xl p-4 relative bg-vf-bg border border-vf-surface"
                    >
                      <button
                        onClick={() => removeListItem("workExperience", i)}
                        className="absolute top-2 right-2 text-vf-danger hover:opacity-80 text-sm"
                      >
                        &times;
                      </button>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="input-label">Job Title</label>
                          <input
                            className="input w-full mt-1"
                            value={w.title || ""}
                            onChange={(e) =>
                              handleListChange(
                                "workExperience",
                                i,
                                "title",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div>
                          <label className="input-label">Company</label>
                          <input
                            className="input w-full mt-1"
                            value={w.company || ""}
                            onChange={(e) =>
                              handleListChange(
                                "workExperience",
                                i,
                                "company",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div>
                          <label className="input-label">Location</label>
                          <input
                            className="input w-full mt-1"
                            value={w.location || ""}
                            onChange={(e) =>
                              handleListChange(
                                "workExperience",
                                i,
                                "location",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div>
                          <label className="input-label">Start Date</label>
                          <input
                            className="input w-full mt-1"
                            value={w.start_date || ""}
                            onChange={(e) =>
                              handleListChange(
                                "workExperience",
                                i,
                                "start_date",
                                e.target.value,
                              )
                            }
                            placeholder="e.g. Jan 2020"
                          />
                        </div>
                        <div>
                          <label className="input-label">End Date</label>
                          <input
                            className="input w-full mt-1"
                            value={w.end_date || ""}
                            onChange={(e) =>
                              handleListChange(
                                "workExperience",
                                i,
                                "end_date",
                                e.target.value,
                              )
                            }
                            placeholder="or leave empty if current"
                            disabled={w.is_current}
                          />
                        </div>
                        <div className="flex items-end pb-2">
                          <label className="flex items-center gap-2 text-xs cursor-pointer text-vf-muted">
                            <input
                              type="checkbox"
                              checked={!!w.is_current}
                              onChange={(e) =>
                                handleListChange(
                                  "workExperience",
                                  i,
                                  "is_current",
                                  e.target.checked,
                                )
                              }
                            />{" "}
                            Currently working here
                          </label>
                        </div>
                      </div>
                      <div className="mt-2">
                        <label className="input-label">Description</label>
                        <textarea
                          className="textarea w-full mt-1"
                          rows={2}
                          value={w.description || ""}
                          onChange={(e) =>
                            handleListChange(
                              "workExperience",
                              i,
                              "description",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                    </div>
                  ))}
                  {(form.workExperience || []).length === 0 && (
                    <p className="text-xs text-center py-3 text-vf-muted">
                      No work experience added yet.
                    </p>
                  )}
                </div>
              ) : (
                <ul className="space-y-4 mt-2">
                  {c.workExperience.map((w, i) => (
                    <li key={i} className="pl-4 border-l-2 border-vf-primary">
                      <div className="text-sm font-semibold text-vf-base">
                        {w.title || "\u2014"}
                      </div>
                      <div className="text-xs text-vf-muted">
                        {w.company}
                        {w.location ? ` \u00b7 ${w.location}` : ""}
                      </div>
                      <div className="text-xs mt-0.5 text-vf-muted opacity-70">
                        {[w.start_date, w.is_current ? "Present" : w.end_date]
                          .filter(Boolean)
                          .join(" \u2013 ")}
                      </div>
                      {w.description && (
                        <p className="text-xs mt-1 text-vf-muted">
                          {w.description}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          )}

          {/* Education */}
          {(editing || (c.education && c.education.length > 0)) && (
            <motion.div className="card p-5" variants={fadeUp}>
              <div className="flex items-center justify-between">
                <SectionHeader>Education</SectionHeader>
                {editing && (
                  <button
                    onClick={() =>
                      addListItem("education", {
                        degree: "",
                        field: "",
                        institution: "",
                        start_year: "",
                        end_year: "",
                        grade: "",
                      })
                    }
                    className="btn-ghost btn-sm text-xs"
                  >
                    + Add
                  </button>
                )}
              </div>
              {editing ? (
                <div className="space-y-4 mt-2">
                  {(form.education || []).map((ed, i) => (
                    <div
                      key={i}
                      className="rounded-xl p-4 relative bg-vf-bg border border-vf-surface"
                    >
                      <button
                        onClick={() => removeListItem("education", i)}
                        className="absolute top-2 right-2 text-vf-danger hover:opacity-80 text-sm"
                      >
                        &times;
                      </button>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="input-label">Degree</label>
                          <input
                            className="input w-full mt-1"
                            value={ed.degree || ""}
                            onChange={(e) =>
                              handleListChange(
                                "education",
                                i,
                                "degree",
                                e.target.value,
                              )
                            }
                            placeholder="e.g. B.Tech"
                          />
                        </div>
                        <div>
                          <label className="input-label">Field</label>
                          <input
                            className="input w-full mt-1"
                            value={ed.field || ""}
                            onChange={(e) =>
                              handleListChange(
                                "education",
                                i,
                                "field",
                                e.target.value,
                              )
                            }
                            placeholder="e.g. Computer Science"
                          />
                        </div>
                        <div>
                          <label className="input-label">Institution</label>
                          <input
                            className="input w-full mt-1"
                            value={ed.institution || ""}
                            onChange={(e) =>
                              handleListChange(
                                "education",
                                i,
                                "institution",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div>
                          <label className="input-label">Start Year</label>
                          <input
                            className="input w-full mt-1"
                            value={ed.start_year || ""}
                            onChange={(e) =>
                              handleListChange(
                                "education",
                                i,
                                "start_year",
                                e.target.value,
                              )
                            }
                            placeholder="e.g. 2016"
                          />
                        </div>
                        <div>
                          <label className="input-label">End Year</label>
                          <input
                            className="input w-full mt-1"
                            value={ed.end_year || ""}
                            onChange={(e) =>
                              handleListChange(
                                "education",
                                i,
                                "end_year",
                                e.target.value,
                              )
                            }
                            placeholder="e.g. 2020"
                          />
                        </div>
                        <div>
                          <label className="input-label">Grade / CGPA</label>
                          <input
                            className="input w-full mt-1"
                            value={ed.grade || ""}
                            onChange={(e) =>
                              handleListChange(
                                "education",
                                i,
                                "grade",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {(form.education || []).length === 0 && (
                    <p className="text-xs text-center py-3 text-vf-muted">
                      No education added yet.
                    </p>
                  )}
                </div>
              ) : (
                <ul className="space-y-3 mt-2">
                  {c.education.map((e, i) => (
                    <li key={i} className="pl-4 border-l-2 border-vf-primary">
                      <div className="text-sm font-semibold text-vf-base">
                        {e.degree}
                        {e.field ? ` \u00b7 ${e.field}` : ""}
                      </div>
                      <div className="text-xs text-vf-muted">
                        {e.institution}
                        {(e.start_year || e.end_year) &&
                          ` \u00b7 ${e.start_year || ""}${e.start_year && e.end_year ? "\u2013" : ""}${e.end_year || ""}`}
                        {e.grade && ` \u00b7 ${e.grade}`}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          )}

          {/* Certifications */}
          {(editing || (c.certifications && c.certifications.length > 0)) && (
            <motion.div className="card p-5" variants={fadeUp}>
              <div className="flex items-center justify-between">
                <SectionHeader>Certifications</SectionHeader>
                {editing && (
                  <button
                    onClick={() =>
                      addListItem("certifications", {
                        name: "",
                        issuer: "",
                        year: "",
                      })
                    }
                    className="btn-ghost btn-sm text-xs"
                  >
                    + Add
                  </button>
                )}
              </div>
              {editing ? (
                <div className="space-y-3 mt-2">
                  {(form.certifications || []).map((cert, i) => (
                    <div
                      key={i}
                      className="rounded-xl p-4 relative bg-vf-bg border border-vf-surface"
                    >
                      <button
                        onClick={() => removeListItem("certifications", i)}
                        className="absolute top-2 right-2 text-vf-danger hover:opacity-80 text-sm"
                      >
                        &times;
                      </button>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="input-label">Name</label>
                          <input
                            className="input w-full mt-1"
                            value={cert.name || ""}
                            onChange={(e) =>
                              handleListChange(
                                "certifications",
                                i,
                                "name",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div>
                          <label className="input-label">Issuer</label>
                          <input
                            className="input w-full mt-1"
                            value={cert.issuer || ""}
                            onChange={(e) =>
                              handleListChange(
                                "certifications",
                                i,
                                "issuer",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div>
                          <label className="input-label">Year</label>
                          <input
                            className="input w-full mt-1"
                            value={cert.year || ""}
                            onChange={(e) =>
                              handleListChange(
                                "certifications",
                                i,
                                "year",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {(form.certifications || []).length === 0 && (
                    <p className="text-xs text-center py-3 text-vf-muted">
                      No certifications added yet.
                    </p>
                  )}
                </div>
              ) : (
                <ul className="space-y-2 mt-2">
                  {c.certifications.map((cert, i) => (
                    <li key={i} className="text-sm">
                      <div className="font-medium text-vf-base">
                        {cert.name}
                      </div>
                      <div className="text-xs text-vf-muted">
                        {[cert.issuer, cert.year]
                          .filter(Boolean)
                          .join(" \u00b7 ")}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          )}

          {/* Projects (read-only) */}
          {!editing && c.projects && c.projects.length > 0 && (
            <motion.div className="card p-5" variants={fadeUp}>
              <SectionHeader>Projects</SectionHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                {c.projects.map((p, i) => (
                  <div
                    key={i}
                    className="rounded-xl p-4 bg-vf-bg border border-vf-surface"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-sm text-vf-base">
                        {p.name}
                      </div>
                      {p.url && (
                        <a
                          href={p.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-vf-accent hover:underline"
                        >
                          Link
                        </a>
                      )}
                    </div>
                    {p.description && (
                      <p className="text-xs mt-1 text-vf-muted">
                        {p.description}
                      </p>
                    )}
                    {p.tech_stack && p.tech_stack.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {p.tech_stack.map((t, j) => (
                          <span key={j} className="badge-brand">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Languages & Awards (read-only) */}
          {!editing &&
            ((c.languages && c.languages.length > 0) ||
              (c.awards && c.awards.length > 0)) && (
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
                variants={fadeUp}
              >
                {c.languages && c.languages.length > 0 && (
                  <div className="card p-5">
                    <SectionHeader>Languages</SectionHeader>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {c.languages.map((l, i) => (
                        <span key={i} className="badge-brand">
                          {l}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {c.awards && c.awards.length > 0 && (
                  <div className="card p-5">
                    <SectionHeader>Awards</SectionHeader>
                    <ul className="text-xs list-disc pl-4 space-y-1 mt-2 text-vf-muted">
                      {c.awards.map((a, i) => (
                        <li key={i}>{a}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.div>
            )}

          {/* Notes - commented out, only resume-parse/items API in use */}
          {/* <motion.div className="card p-5" variants={fadeUp}>
            <div className="flex items-center justify-between">
              <SectionHeader>Notes</SectionHeader>
              <span className="text-xs text-vf-muted">{notes.length}</span>
            </div>
            {loadingNotes ? (
              <div className="text-center py-4">
                <div className="skeleton h-3 w-20 mx-auto rounded" />
              </div>
            ) : notes.length === 0 ? (
              <div className="text-center text-xs py-4 text-vf-muted">
                No notes yet.
              </div>
            ) : (
              <ul className="space-y-2 mt-2">
                {notes.map((n) => {
                  const t =
                    NOTE_TYPES.find((x) => x.value === n.noteType) ||
                    NOTE_TYPES[0];
                  return (
                    <li
                      key={n.id}
                      className="group rounded-xl p-3 relative bg-vf-bg border border-vf-surface"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={t.badge}>{t.label}</span>
                        <span className="text-[10px] text-vf-muted">
                          {n.createdAt
                            ? new Date(n.createdAt).toLocaleString()
                            : ""}
                        </span>
                      </div>
                      <p className="text-xs whitespace-pre-wrap text-vf-base">
                        {n.text}
                      </p>
                      {n.author && (
                        <div className="mt-1 text-[10px] text-vf-muted">
                          &mdash; {n.author}
                        </div>
                      )}
                      <button
                        onClick={() => delNote(n.id)}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-sm leading-none transition-opacity text-vf-muted hover:text-vf-danger"
                      >
                        &times;
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="mt-3 flex gap-2">
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add a note\u2026"
                rows={2}
                className="textarea flex-1"
              />
              <div className="flex flex-col gap-1">
                <select
                  value={noteType}
                  onChange={(e) => setNoteType(e.target.value)}
                  className="select text-xs"
                >
                  {NOTE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={addNote}
                  disabled={!noteText.trim() || addingNote}
                  className="btn-primary btn-sm"
                >
                  {addingNote ? "Adding..." : "Add"}
                </button>
              </div>
            </div>
          </motion.div> */}

          {/* Journey - commented out, only resume-parse/items API in use */}
          {/* <motion.div className="card p-5" variants={fadeUp}>
            <SectionHeader>Journey</SectionHeader>
            {loadingJourney ? (
              <div className="space-y-3 mt-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="skeleton w-3 h-3 rounded-full mt-0.5 shrink-0" />
                    <div className="flex-1 space-y-1">
                      <div className="skeleton h-3 w-32 rounded" />
                      <div className="skeleton h-2 w-48 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : journey.length === 0 ? (
              <div className="text-center text-xs py-4 text-vf-muted">
                No journey events yet.
              </div>
            ) : (
              <div className="relative mt-2 ml-1.5">
                <div className="absolute left-[5px] top-1 bottom-1 w-px bg-vf-surface" />
                <ul className="space-y-4">
                  {journey.map((evt, i) => {
                    const style = JOURNEY_EVENT_STYLES[evt.eventType] || {
                      color: "bg-vf-muted",
                      label: evt.eventType,
                    };
                    return (
                      <li
                        key={evt.id || i}
                        className="relative flex items-start gap-3 pl-5"
                      >
                        <span
                          className={`absolute left-0 top-1 w-[11px] h-[11px] rounded-full border-2 border-white ${style.color}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-vf-base">
                              {evt.title || style.label}
                            </span>
                            {evt.eventType === "INTERVIEW_SCORED" &&
                              evt.score != null && (
                                <span className="badge-success text-2xs">
                                  {evt.score}%
                                </span>
                              )}
                          </div>
                          {evt.details && (
                            <p className="text-xs mt-0.5 text-vf-muted">
                              {evt.details}
                            </p>
                          )}
                          <span className="text-[10px] text-vf-muted mt-0.5 block">
                            {evt.occurredAt
                              ? new Date(evt.occurredAt).toLocaleString()
                              : ""}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </motion.div> */}

          {/* Raw text excerpt — collapsed by default per spec §7 */}
          {rawExcerpt && (
            <motion.div className="card p-5" variants={fadeUp}>
              <button
                onClick={() => setShowRawExcerpt((v) => !v)}
                className="w-full flex items-center justify-between text-left"
              >
                <SectionHeader>Raw Text Excerpt</SectionHeader>
                <span className="text-xs text-vf-muted">
                  {showRawExcerpt ? "Hide" : "Show"}
                </span>
              </button>
              {showRawExcerpt && (
                <pre className="mt-2 text-xs whitespace-pre-wrap text-vf-muted bg-vf-bg border border-vf-surface rounded-lg p-3 max-h-80 overflow-auto">
                  {rawExcerpt}
                </pre>
              )}
            </motion.div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

const InfoField = ({ label, value }) => {
  return (
    <div>
      <div className="text-xs text-vf-muted">{label}</div>
      <div className="text-sm font-medium mt-0.5 text-vf-base">
        {value || "\u2014"}
      </div>
    </div>
  );
};

const SectionHeader = ({ children }) => {
  return (
    <div className="text-[10px] uppercase tracking-wider mb-2 font-semibold text-vf-muted font-heading">
      {children}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    pending: { label: "Pending", cls: "badge-brand" },
    parsing: { label: "Parsing\u2026", cls: "badge-warning" },
    done: { label: "Done", cls: "badge-success" },
    error: { label: "Error", cls: "badge-danger" },
  };
  const v = map[status] || map.pending;
  return <span className={v.cls}>{v.label}</span>;
};

// Map the AI service item status to one of the four StatusBadge keys.
const mapItemStatusToBadge = (status) => {
  switch (status) {
    case "QUEUED":
      return "pending";
    case "PROCESSING":
      return "parsing";
    case "COMPLETED":
      return "done";
    case "FAILED":
      return "error";
    default:
      return "pending";
  }
};

const formatRelative = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!then) return "";
  const sec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  return `${hr}h ago`;
};

// Compact row used in the "Parsed candidates" table. Keeps the original item id
// so detail / delete / invite can still address it through the AI service.
const itemToCandidateRow = (item) => ({
  id: item.id,
  fileName: item.fileName,
  status: item.status,
  candidateName: item.extractedName || item.fileName,
  candidateEmail: item.extractedEmail || "",
  candidatePhone: item.extractedPhone || "",
  currentRole: null,
  currentLocation: null,
  totalYearsExperience: null,
  createdAt: item.completedAt || item.startedAt || item.queuedAt || null,
});

// Inflate the full item (incl. parsedJson) into the candidate-shaped object the
// drawer's existing UI is built around. Per spec \u00a73.3 every field may be null
// or absent, so we mirror that with sensible defaults.
const normalizeItemToCandidate = (item) => {
  let parsed = {};
  if (item && typeof item.parsedJson === "string" && item.parsedJson.length) {
    try {
      parsed = JSON.parse(item.parsedJson);
    } catch {
      parsed = {};
    }
  } else if (item && item.parsedJson && typeof item.parsedJson === "object") {
    parsed = item.parsedJson;
  }
  const personal = parsed.personal || {};
  return {
    id: item.id,
    fileName: item.fileName,
    status: item.status,
    candidateName:
      personal.fullName || item.extractedName || item.fileName || "",
    candidateEmail: personal.email || item.extractedEmail || "",
    candidatePhone: personal.phone || item.extractedPhone || "",
    currentLocation: personal.location || null,
    preferredLocation: null,
    linkedinUrl: personal.linkedinUrl || null,
    portfolioUrl: personal.portfolioUrl || null,
    githubUrl: null,
    summary: parsed.summary || "",
    totalYearsExperience:
      typeof parsed.totalExperienceYears === "number"
        ? parsed.totalExperienceYears
        : null,
    noticePeriodDays: null,
    currentCtcLpa: null,
    expectedCtcLpa: null,
    educationLevel: null,
    currentRole:
      (Array.isArray(parsed.experience) && parsed.experience[0]?.title) || null,
    currentCompany:
      (Array.isArray(parsed.experience) && parsed.experience[0]?.company) ||
      null,
    skills: Array.isArray(parsed.skills) ? parsed.skills : [],
    workExperience: (Array.isArray(parsed.experience)
      ? parsed.experience
      : []
    ).map((w) => ({
      title: w.title || "",
      company: w.company || "",
      location: w.location || "",
      start_date: w.startDate || "",
      end_date: w.endDate || "",
      is_current: !!w.isCurrent,
      description: w.description || "",
    })),
    education: (Array.isArray(parsed.education) ? parsed.education : []).map(
      (e) => ({
        degree: e.degree || "",
        field: e.fieldOfStudy || "",
        institution: e.institution || "",
        start_year: e.startYear || "",
        end_year: e.endYear || "",
        grade: e.grade || "",
      }),
    ),
    projects: (Array.isArray(parsed.projects) ? parsed.projects : []).map(
      (p) => ({
        name: p.name || "",
        description: p.description || "",
        tech_stack: Array.isArray(p.techStack) ? p.techStack : [],
        url: p.url || null,
        start_date: p.startDate || "",
        end_date: p.endDate || "",
      }),
    ),
    certifications: (Array.isArray(parsed.certifications)
      ? parsed.certifications
      : []
    ).map((c) => ({
      name: c.name || "",
      issuer: c.issuer || "",
      year: c.issueYear || c.year || "",
    })),
    languages: Array.isArray(parsed.languages) ? parsed.languages : [],
    awards: [],
    _rawTextExcerpt: parsed.rawTextExcerpt || null,
  };
};

export default UploadResumes;
